
import { useState } from "react";
import { MoodRatingSection } from "./MoodRatingSection";
import { JournalTextArea } from "./JournalTextArea";
import { MoodTags } from "./MoodTags";
import { JournalFormActions } from "./JournalFormActions";
import { VerseSuggestionDialog } from "./VerseSuggestionDialog";

interface JournalFormProps {
  onSubmit: (data: {
    content: string;
    moodRating: string;
    selectedTags: string[];
  }) => boolean;
  onSuggestVerse?: (data: {
    content: string;
    moodRating: string;
    selectedTags: string[];
  }) => Promise<{ verse: string; reference: string; explanation: string; prayer: string; }>;
  disabled?: boolean;
}

export function JournalForm({ onSubmit, onSuggestVerse, disabled = false }: JournalFormProps) {
  const [content, setContent] = useState("");
  const [moodRating, setMoodRating] = useState<string>("3");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showVerseDialog, setShowVerseDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedVerse, setSuggestedVerse] = useState<{
    verse: string;
    reference: string;
    explanation: string;
    prayer: string;
  } | null>(null);

  const validateForm = () => {
    if (!content.trim()) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || disabled) return;
    
    setIsLoading(true);
    try {
      // Submit the journal entry and check if it was successful
      const success = onSubmit({ content, moodRating, selectedTags });
      
      if (success) {
        // Then get verse suggestion if available
        if (onSuggestVerse) {
          const suggestion = await onSuggestVerse({ 
            content, 
            moodRating, 
            selectedTags 
          });
          setSuggestedVerse(suggestion);
          setShowVerseDialog(true);
        }

        // Reset form
        setContent("");
        setMoodRating("3");
        setSelectedTags([]);
      }
    } catch (error) {
      console.error('Error saving journal entry:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <MoodRatingSection 
        value={moodRating} 
        onChange={setMoodRating} 
        disabled={disabled}
      />

      <JournalTextArea 
        value={content} 
        onChange={(e) => setContent(e.target.value)} 
        disabled={disabled}
      />

      <MoodTags 
        selected={selectedTags} 
        onSelect={setSelectedTags}
        moodRating={moodRating}
        disabled={disabled}
      />

      <JournalFormActions 
        onSubmit={handleSubmit}
        isLoading={isLoading}
        disabled={disabled}
      />

      {suggestedVerse && (
        <VerseSuggestionDialog
          isOpen={showVerseDialog}
          onClose={() => setShowVerseDialog(false)}
          verse={suggestedVerse.verse}
          reference={suggestedVerse.reference}
          explanation={suggestedVerse.explanation}
          prayer={suggestedVerse.prayer}
        />
      )}
    </form>
  );
}
